import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { PR_STATUS_LABELS } from '../lib/format';
import type { SearchResults } from '../lib/types';
import { Icon } from './icons';

interface Hit { key: string; to: string; label: string; meta: string }

const DELAY_MS = 200;

/** Results in one list: items, then purchase orders, purchase requests and suppliers. */
export function toHits(r: SearchResults): Hit[] {
  return [
    ...r.items.map(i => ({ key: `i-${i.id}`, to: `/items/${i.id}`, label: i.name, meta: `Item · ${i.sku}${i.active ? '' : ' · inactive'}` })),
    ...r.purchaseOrders.map(o => ({ key: `o-${o.id}`, to: `/purchase-orders/${o.id}`, label: o.netsuite_po_number ? `${o.number} · ${o.netsuite_po_number}` : o.number, meta: `Purchase order · ${o.supplier_name}` })),
    ...r.purchaseRequests.map(p => ({ key: `r-${p.id}`, to: `/purchase-requests/${p.id}`, label: p.number, meta: `Purchase request · ${PR_STATUS_LABELS[p.status] ?? p.status}${p.supplier_name ? ` · ${p.supplier_name}` : ''}` })),
    ...r.suppliers.map(s => ({ key: `s-${s.id}`, to: `/suppliers/${s.id}`, label: s.name, meta: `Supplier${s.active ? '' : ' · inactive'}` })),
  ];
}

/**
 * Top bar search (items by name or SKU, POs by number or NetSuite PO no., PRs, suppliers). A combobox: type two
 * characters, arrows move through the results, Enter opens one, Escape clears.
 */
export function GlobalSearch({ autoFocus, onDone }: { autoFocus?: boolean; onDone?: () => void }) {
  const propertyId = useSession().property?.id;
  const navigate = useNavigate();
  const listId = useId();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const call = useRef(0);

  useEffect(() => {
    const term = q.trim();
    const id = ++call.current;
    if (term.length < 2 || !propertyId) {
      setHits(null);
      return;
    }
    const timer = setTimeout(() => {
      api.get<SearchResults>('/search', { propertyId, q: term })
        .then(r => { if (id === call.current) { setHits(toHits(r)); setActive(0); } })
        .catch(() => { if (id === call.current) setHits([]); });
    }, DELAY_MS);
    return () => clearTimeout(timer);
  }, [q, propertyId]);

  const go = (hit: Hit) => {
    setQ('');
    setOpen(false);
    navigate(hit.to);
    onDone?.();
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setQ('');
      setOpen(false);
      return;
    }
    if (!hits?.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActive(a => (a + (e.key === 'ArrowDown' ? 1 : hits.length - 1)) % hits.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(hits[active]);
    }
  };

  const shown = open && hits !== null;
  return (
    <div className="globalsearch" role="search">
      <Icon name="search" />
      <input
        type="search" role="combobox" aria-label="Search" placeholder="Search items, POs, suppliers" autoComplete="off" autoFocus={autoFocus}
        aria-expanded={shown} aria-controls={listId} aria-autocomplete="list" aria-activedescendant={shown && hits.length ? `${listId}-${active}` : undefined}
        value={q} onChange={e => { setQ(e.target.value); setOpen(true); }} onKeyDown={onKey} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
      />
      {shown && (
        <ul id={listId} role="listbox" aria-label="Search results" className="comboboxlist" onMouseDown={e => e.preventDefault()}>
          {hits.length ? hits.map((h, i) => (
            <li key={h.key} id={`${listId}-${i}`} role="option" aria-selected={i === active} className={i === active ? 'active' : undefined} onClick={() => go(h)}>
              <span>{h.label}</span><span className="muted">{h.meta}</span>
            </li>
          )) : <li className="empty" role="option" aria-selected={false} aria-disabled="true">No matches</li>}
        </ul>
      )}
    </div>
  );
}
